import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, ArrowRight, Lock } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { getPublicArticles } from "@/lib/articles";
import { AcademyCTA } from "@/components/site/article/AcademyCTA";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";
import educationHeroBg from "@/assets/education-hero-bg.webp";

const SITE = "https://xauproacademy.com";

export const Route = createFileRoute("/education/")({
  head: () => {
    const list = getPublicArticles();
    return {
      meta: [
        { title: "Education Hub | Master the Gold Market" },
        {
          name: "description",
          content:
            "Smart Money Concepts education, market structure analysis and weekly XAU/USD outlooks for serious gold traders.",
        },
        { property: "og:title", content: "Education Hub, SMC Trading Lessons" },
        {
          property: "og:description",
          content:
            "Long-form analysis, trade breakdowns and the methodology behind every XAU/USD signal we publish.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE}/education` },
      ],
      links: [{ rel: "canonical", href: `${SITE}/education` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "XAU Pro Academy Education Hub",
            url: `${SITE}/education`,
            description:
              "Smart Money Concepts education and XAU/USD market analysis.",
            blogPost: list.map((a) => ({
              "@type": "BlogPosting",
              headline: a.title,
              description: a.metaDescription,
              datePublished: a.publishedAt,
              dateModified: a.updatedAt,
              author: { "@type": "Organization", name: a.author },
              url: `${SITE}/education/${a.slug}`,
            })),
          }),
        },
      ],
    };
  },
  component: EducationPage,
});

function EducationPage() {
  const all = getPublicArticles();
  const publicArticles = all.filter((a) => a.visibility !== "preview");
  const previewLessons = all.filter((a) => a.visibility === "preview");
  const [featured, ...unsorted] = publicArticles;
  // Featured stays pinned; the grid below runs newest first.
  const rest = [...unsorted].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <img
          src={educationHeroBg}
          alt="XAU Pro Academy gold trading education library and Smart Money Concepts guides"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity"
        />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[32rem] w-[56rem] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, hsl(var(--gold) / 0.45), transparent)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/55 to-background" />

        <div className="container relative mx-auto max-w-7xl px-4 pb-24 pt-20 md:pt-32">
          <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
            <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
              Education Library
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl lg:text-7xl">
              XAU Pro <span className="text-gradient-gold">Education</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
              Free long-form analysis and trade breakdowns above. Premium Academy
              lesson previews below.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild variant="gold" size="lg">
                <a href="#articles">Browse Articles</a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/pricing">Enter the Academy</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SMC pillar anchor */}
      <section className="border-y border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-7xl px-4 py-10">
          <Link
            to="/smart-money-concepts"
            className="group flex flex-col gap-4 rounded-xl border border-border/60 bg-background/40 p-6 transition hover:border-gold/40 md:flex-row md:items-center md:justify-between"
          >
            <div className="max-w-2xl">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                Anchor Pillar
              </div>
              <div className="mt-2 font-display text-xl font-semibold text-foreground md:text-2xl">
                Smart Money Concepts for XAUUSD
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                The doctrine that organizes every article in this library. Start
                here if you want the framework before the case studies.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
              Read the pillar
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </span>
          </Link>
        </div>
      </section>

      <section id="articles" className="container mx-auto max-w-7xl px-4 py-14">

        {/* Featured */}
        {featured && (
          <Link
            to="/education/$slug"
            params={{ slug: featured.slug }}
            className="group relative mb-12 block overflow-hidden rounded-2xl border border-border/60 bg-card transition hover:border-gold/40"
          >
            <div className="grid md:grid-cols-2">
              <div
                className={`relative aspect-video overflow-hidden bg-gradient-to-br ${featured.coverGradient} md:aspect-auto`}
              >
                {featured.heroImage ? (
                  <img
                    src={featured.heroImage}
                    alt={featured.title}
                    width={1536}
                    height={864}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 grid-bg opacity-40" />
                    <div className="absolute inset-0 flex items-center justify-center font-display text-7xl font-bold text-gradient-gold opacity-30">
                      SMC
                    </div>
                  </>
                )}
              </div>
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-3 text-xs">
                  <span className="rounded-full bg-gold-muted px-3 py-1 font-semibold uppercase tracking-wider text-gold">
                    Featured
                  </span>
                  <span className="text-muted-foreground">{featured.category}</span>
                </div>
                <h2 className="mt-4 font-display text-2xl font-bold leading-tight md:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-3 text-muted-foreground">{featured.excerpt}</p>
                <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-5">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>
                      {new Date(featured.publishedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {featured.readTime}
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-gold">
                    Read{" "}
                    <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Free articles grid */}
        {rest.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((a) => (
              <Link
                key={a.slug}
                to="/education/$slug"
                params={{ slug: a.slug }}
                className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gold">
                  <span>{a.category}</span>
                </div>
                <h3 className="mt-3 font-display text-lg font-bold leading-snug">
                  {a.title}
                </h3>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{a.excerpt}</p>
                <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4 text-xs text-muted-foreground">
                  <span>
                    {new Date(a.publishedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {a.readTime}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {publicArticles.length === 0 && (
          <div className="rounded-xl border border-dashed border-border/60 bg-card/40 p-10 text-center">
            <p className="text-muted-foreground">
              More lessons are dropping soon. Subscribe to be the first to read them.
            </p>
          </div>
        )}

        {/* Academy lesson previews (paid Modules 1, 2, 3) */}
        {previewLessons.length > 0 && (
          <div className="mt-20">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
                <Lock className="h-3 w-3" /> Academy Members Only
              </span>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight md:text-4xl">
                Premium lessons from the <span className="text-gradient-gold">Academy</span>
              </h2>
              <p className="mt-3 text-sm text-muted-foreground md:text-base">
                These lessons live inside Modules 1 to 3 of the XAU Pro Academy. Read the
                opening preview below. The full lesson, examples, and frameworks unlock with
                a membership.
              </p>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {previewLessons.map((a) => (
                <Link
                  key={a.slug}
                  to="/education/$slug"
                  params={{ slug: a.slug }}
                  className="group relative flex flex-col rounded-xl border border-gold/30 bg-gradient-to-br from-gold-muted/40 via-card to-card p-6 transition hover:border-gold/60"
                >
                  <div className="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-gold">
                    <span>{a.category}</span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-background/40 px-2 py-0.5 text-[10px]">
                      <Lock className="h-3 w-3" /> Preview
                    </span>
                  </div>
                  <h3 className="mt-3 font-display text-lg font-bold leading-snug">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
                    {a.previewExcerpt ?? a.excerpt}
                  </p>
                  {a.previewTakeaways && a.previewTakeaways.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {a.previewTakeaways.slice(0, 3).map((t) => (
                        <li
                          key={t}
                          className="flex gap-2 text-xs text-muted-foreground"
                        >
                          <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-gold" />
                          <span className="line-clamp-2">{t}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4 text-xs">
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Clock className="h-3 w-3" /> {a.readTime}
                    </span>
                    <span className="inline-flex items-center gap-1 font-semibold text-gold">
                      Read preview
                      <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-10 flex justify-center">
              <Button asChild variant="gold" size="lg">
                <Link to="/pricing">Unlock all lessons</Link>
              </Button>
            </div>
          </div>
        )}

        <div className="-mx-4 mt-16">
          <MacroToolsStrip />
        </div>

        <AcademyCTA variant="feed" />
      </section>
    </Layout>
  );
}

